'use client';
import React from 'react';
import { X, Phone, Mail, MapPin, Zap, Calendar, DollarSign, Target, MessageSquare } from 'lucide-react';
import LeverageBadge from '@/components/ui/LeverageBadge';
import StatusBadge from '@/components/ui/StatusBadge';
import AppImage from '@/components/ui/AppImage';
import { Lead } from './mockLeads';

interface Props {
  lead: Lead;
  onClose: () => void;
  onStatusChange: (status: Lead['status']) => void;
}

export default function LeadDetailModal({ lead, onClose, onStatusChange }: Props) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fade-in">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#111111] border border-[#2A2A2A] rounded-t-2xl sm:rounded-xl w-full sm:max-w-2xl shadow-modal animate-slide-up max-h-[92vh] overflow-y-auto scrollbar-thin">
        {/* Header */}
        <div className="sticky top-0 bg-[#111111] border-b border-[#1F1F1F] px-5 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-blue-600/20 flex items-center justify-center text-blue-400 font-700 text-sm">
              {lead.firstName[0]}{lead.lastName[0]}
            </div>
            <div>
              <h3 className="text-white font-700 text-base">{lead.firstName} {lead.lastName}</h3>
              <p className="text-[#555] text-xs">{lead.projectType}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LeverageBadge level={lead.leverageScore} />
            <button onClick={onClose} className="w-8 h-8 rounded-lg bg-[#1A1A1A] hover:bg-[#2A2A2A] flex items-center justify-center text-[#666] hover:text-white transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {/* Photo */}
          <div className="relative w-full h-52 rounded-xl overflow-hidden bg-[#0D0D0D] border border-[#2A2A2A]">
            <AppImage
              src={lead.photoUrl}
              alt={lead.photoAlt}
              fill
              className="object-cover"
              sizes="(max-width: 672px) 100vw, 672px"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
              <p className="text-xs text-[#AAA]">Project photo · Submitted {new Date(lead.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
            </div>
          </div>

          {/* Estimate highlight */}
          <div className="flex items-center gap-4 p-4 bg-amber-500/8 border border-amber-500/20 rounded-xl">
            <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center flex-shrink-0">
              <DollarSign size={20} className="text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[#888] text-xs font-500 uppercase tracking-wide mb-0.5">AI Estimate</p>
              <p className="text-2xl font-800 text-white tabular-nums">
                ${lead.estimateMin.toLocaleString()} – ${lead.estimateMax.toLocaleString()}
              </p>
            </div>
            <StatusBadge status={lead.status} />
          </div>

          {/* Score reason */}
          <div className="flex items-start gap-3 p-4 bg-[#0D0D0D] border border-[#1F1F1F] rounded-xl">
            <Zap size={16} className="text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-[#666] text-xs font-500 uppercase tracking-wide mb-1">Leverage Analysis</p>
              <p className="text-[#CCC] text-sm leading-relaxed">{lead.scoreReason}</p>
            </div>
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: <DollarSign size={14} />, label: 'Budget', value: lead.budgetRange },
              { icon: <Calendar size={14} />, label: 'Timeline', value: lead.timeline },
              { icon: <Target size={14} />, label: 'Priority', value: lead.priority.split('—')[0].trim() },
              { icon: <Calendar size={14} />, label: 'Submitted', value: new Date(lead.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) },
            ].map(({ icon, label, value }) => (
              <div key={`detail-${label}`} className="p-3 bg-[#0D0D0D] border border-[#1F1F1F] rounded-lg">
                <div className="flex items-center gap-1.5 mb-1 text-[#555]">
                  {icon}
                  <span className="text-[11px] uppercase tracking-wide font-500">{label}</span>
                </div>
                <p className="text-white text-sm font-600">{value}</p>
              </div>
            ))}
          </div>

          {/* Contact */}
          <div className="space-y-2">
            <p className="text-[11px] text-[#555] uppercase tracking-wide font-500">Contact Details</p>
            <div className="flex items-center gap-3 p-3 bg-[#0D0D0D] border border-[#1F1F1F] rounded-lg">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                <Phone size={14} className="text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[#555] text-xs">Phone</p>
                <p className="text-white text-sm font-600">{lead.phone}</p>
              </div>
              <a href={`tel:${lead.phone}`} className="px-3 py-1.5 bg-emerald-600/20 border border-emerald-600/30 rounded-lg text-emerald-400 text-xs font-600 hover:bg-emerald-600/30 transition-colors">
                Call
              </a>
            </div>
            <div className="flex items-center gap-3 p-3 bg-[#0D0D0D] border border-[#1F1F1F] rounded-lg">
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <Mail size={14} className="text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[#555] text-xs">Email</p>
                <p className="text-white text-sm font-600 truncate">{lead.email}</p>
              </div>
              <a href={`mailto:${lead.email}`} className="px-3 py-1.5 bg-blue-600/20 border border-blue-600/30 rounded-lg text-blue-400 text-xs font-600 hover:bg-blue-600/30 transition-colors">
                Email
              </a>
            </div>
            <div className="flex items-start gap-3 p-3 bg-[#0D0D0D] border border-[#1F1F1F] rounded-lg">
              <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <MapPin size={14} className="text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[#555] text-xs">Project Address</p>
                <p className="text-white text-sm font-600 leading-relaxed">{lead.address}</p>
              </div>
            </div>
          </div>

          {/* Notes */}
          {lead.notes && (
            <div className="flex items-start gap-3 p-4 bg-[#0D0D0D] border border-[#1F1F1F] rounded-xl">
              <MessageSquare size={15} className="text-[#555] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[#555] text-xs uppercase tracking-wide font-500 mb-1">Notes</p>
                <p className="text-[#AAA] text-sm leading-relaxed">{lead.notes}</p>
              </div>
            </div>
          )}

          {/* Status change */}
          <div>
            <p className="text-[11px] text-[#555] uppercase tracking-wide font-500 mb-2">Update Status</p>
            <div className="flex flex-wrap gap-2">
              {(['New', 'Reviewed', 'Contacted', 'Won', 'Rejected'] as Lead['status'][]).map((s) => (
                <button
                  key={`modal-status-${s}`}
                  onClick={() => onStatusChange(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-600 border transition-all duration-150 ${
                    lead.status === s
                      ? 'bg-blue-600 border-blue-600 text-white' :'bg-[#0D0D0D] border-[#2A2A2A] text-[#666] hover:border-[#3A3A3A] hover:text-white'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3 pt-2">
            <a
              href={`tel:${lead.phone}`}
              className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 active:scale-95 text-white font-600 rounded-lg py-3 text-sm transition-all duration-150"
            >
              <Phone size={15} />
              Call Now
            </a>
            <a
              href={`mailto:${lead.email}`}
              className="flex-1 flex items-center justify-center gap-2 leverage-btn-secondary py-3 text-sm"
            >
              <Mail size={15} />
              Send Email
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}